import './kitStyles';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { placeFloat, useFloatPlace } from './floatPlace';
import { isTouchInput } from './inputMode';

/**
 * WEB-3310 юнит 2а-2 — правила открытия и закрытия всплывающих кита (эталон
 * `design/cabinet-rescin/kit.jsx`: `useFlipUp`, `usePopRules`, `sheetRef`,
 * `sheetWrap`, `inOwn`, `tapAct`, `swallowClick`, `useOutside`; правило К-11
 * эталона `ПРАВИЛА.md`).
 *  - Открылось одно — соседнее закрывается: событие `w2a:pop` на `window`
 *    (вложенное — внутри своего хозяина или его шторки — родителя не закрывает).
 *  - Прокрутка человеком (`wheel`, `touchmove`) мимо списка закрывает;
 *    программная прокрутка (`scroll`) и прокрутка внутри списка — нет.
 *  - Щелчок мимо (`mousedown`) и Esc закрывают; касание мимо открытого только
 *    закрывает его — следующий `click` гасится (`swallowClick`, 700 мс); мышью —
 *    так же над `tr`, `.card`, `a`, в остальных местах — с первого раза.
 *  - Телефон (до 640 включительно): список выносится порталом в ближайший `.ds`
 *    (`sheetWrap`) и помнит хозяина (`sheetRef`) — касание по вынесенной
 *    шторке не «мимо» (`inOwn`). Пункт на касании срабатывает по отпусканию
 *    пальца ровно один раз (`tapAct`).
 *  - Касание мимо своего открытого окна со своим обработчиком «мимо» (календарь,
 *    поиск-лупа) — `outTap()` гасит щелчок под пальцем; мышью — ничего (юнит 3,
 *    эталон `kit.jsx:661`; признак касания — `isTouchInput()`, `ds/inputMode.ts`).
 * Глобальных `window.*` эталона нет (`ПЕРЕДАЧА-ФРОНТУ.md` §7).
 */
type HostRef = RefObject<HTMLElement | null>;

const SHEET_OF = new WeakMap<Element, HostRef>();

/** Телефон — `matchMedia('(max-width: 640px)')`. */
export const isPhone = (): boolean =>
  typeof window.matchMedia === 'function' && !!window.matchMedia('(max-width: 640px)')?.matches;

/** Ref-колбэк узла шторки: узел помнит хозяина. */
export const sheetRef =
  (ref: HostRef) =>
  (n: HTMLElement | null): void => {
    if (n) SHEET_OF.set(n, ref);
  };

/**
 * На телефоне — портал в открытое окно хозяина (`dialog[open]`, эталон 24.10), иначе в ближайший `.ds`
 * (нет — `document.body`); на компьютере — на месте.
 */
export function sheetWrap(ref: HostRef, el: React.ReactElement): React.ReactNode {
  const host = isPhone() && ref.current ? ref.current.closest('dialog[open]') || ref.current.closest('.ds') || document.body : null;
  return host ? createPortal(el, host) : el;
}

/** Узел внутри хозяина или внутри его вынесенной шторки. */
export function inOwn(ref: HostRef, t: EventTarget | null): boolean {
  const own = ref.current;
  if (!own || !(t instanceof Node)) return false;
  if (own.contains(t)) return true;
  const el = t instanceof Element ? t : t.parentElement;
  const sh = el?.closest('[data-sheet]');
  return !!sh && SHEET_OF.get(sh) === ref;
}

/** Следующий `click` гасится целиком; не пришёл за 700 мс — снимается. */
export function swallowClick(): void {
  const off = () => {
    document.removeEventListener('click', c, true);
    clearTimeout(t);
  };
  const c = (ev: Event) => {
    ev.preventDefault();
    ev.stopPropagation();
    off();
  };
  document.addEventListener('click', c, true);
  const t = setTimeout(off, 700);
}

/** Касание мимо своего открытого окна: последний указатель — палец — следующий `click` гасится. */
export function outTap(): void {
  if (isTouchInput()) swallowClick();
}

/** Пункт: касанием — по отпусканию пальца (следующий `click` гасится), мышью — по `click`. */
export function tapAct(fn: () => void): {
  onPointerUp: (e: React.PointerEvent) => void;
  onClick: () => void;
} {
  return {
    onPointerUp: (e) => {
      if (e.pointerType === 'mouse' || e.button > 0) return;
      swallowClick();
      fn();
    },
    onClick: fn,
  };
}

/**
 * Открыть вверх: снизу места меньше, чем настоящая высота списка (не больше `h`
 * и 60 % экрана, плюс 16), а сверху больше, чем снизу.
 */
export function useFlipUp(ref: HostRef, open: boolean, h = 360): boolean {
  const [up, setUp] = useState(false);
  useLayoutEffect(() => {
    const own = ref.current;
    if (!open || !own) return;
    const r = own.getBoundingClientRect();
    const pp = own.querySelector('.pop');
    const need = Math.min(pp ? pp.scrollHeight : h, h, window.innerHeight * 0.6) + 16;
    const below = window.innerHeight - r.bottom;
    setUp(below < need && r.top > below);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  return up;
}

/** Открылось одно — соседние закрываются; прокрутка человеком мимо — закрывает. */
export function usePopRules(ref: HostRef, onOut: () => void, active: boolean): void {
  const cb = useRef(onOut);
  cb.current = onOut;
  useEffect(() => {
    if (!active) return undefined;
    window.dispatchEvent(new CustomEvent('w2a:pop', { detail: ref.current }));
    const other = (e: Event) => {
      const el = (e as CustomEvent<HTMLElement | null>).detail;
      if (!ref.current || el === ref.current || (el && inOwn(ref, el))) return;
      cb.current();
    };
    const scr = (e: Event) => {
      const t = e.target;
      if (!ref.current) return;
      if (t instanceof Element && (inOwn(ref, t) || t.closest('.pop, [role=listbox], [role=menu]'))) return;
      cb.current();
    };
    const opts = { capture: true, passive: true };
    const t = setTimeout(() => {
      window.addEventListener('w2a:pop', other);
      document.addEventListener('wheel', scr, opts);
      document.addEventListener('touchmove', scr, opts);
    }, 0);
    return () => {
      clearTimeout(t);
      window.removeEventListener('w2a:pop', other);
      document.removeEventListener('wheel', scr, true);
      document.removeEventListener('touchmove', scr, true);
    };
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps
}

/** Общие правила плюс щелчок мимо, Esc и касание мимо (гасит следующий щелчок). */
export function useOutside(ref: HostRef, onOut: () => void, active: boolean): void {
  usePopRules(ref, onOut, active);
  const cb = useRef(onOut);
  cb.current = onOut;
  useEffect(() => {
    if (!active) return undefined;
    const h = (e: Event) => {
      if (ref.current && !inOwn(ref, e.target)) cb.current();
    };
    // Esc гасится, пока выпадашка открыта: внутри окна кита (<dialog>) непогашенное Esc
    // браузер превращает в `cancel` окна — закрылось бы всё окно (WEB-4072, №12)
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cb.current();
      }
    };
    const p = (e: Event) => {
      const t = e.target;
      const touch = (e as PointerEvent).pointerType !== 'mouse';
      if (ref.current && !inOwn(ref, t) && (touch || (t instanceof Element && t.closest('tr, .card, a')))) {
        swallowClick();
      }
    };
    document.addEventListener('mousedown', h);
    document.addEventListener('keydown', k);
    document.addEventListener('pointerdown', p, true);
    return () => {
      document.removeEventListener('mousedown', h);
      document.removeEventListener('keydown', k);
      document.removeEventListener('pointerdown', p, true);
    };
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * Внутреннее для списков кита: узел списка — место всплывающих (`useFloatPlace`,
 * переставить и при смене «вверх») и память хозяина шторки (`sheetRef`).
 */
export function usePopNode(
  ref: HostRef,
  open: boolean,
  up: boolean,
): { pop: RefObject<HTMLUListElement>; popRef: (n: HTMLUListElement | null) => void } {
  const pop = useRef<HTMLUListElement>(null);
  useFloatPlace(pop, open);
  useLayoutEffect(() => {
    if (open && pop.current) placeFloat(pop.current);
  }, [up]); // eslint-disable-line react-hooks/exhaustive-deps
  const popRef = useCallback(
    (n: HTMLUListElement | null) => {
      (pop as React.MutableRefObject<HTMLUListElement | null>).current = n;
      sheetRef(ref)(n);
    },
    [ref],
  );
  return { pop, popRef };
}
